import type { Identifier } from "ra-core";

import {
  isInLeadManagerScope,
  type Actor,
  type ScopedSale,
} from "../providers/commons/canAccess";

/**
 * Almikia Epic E7 (TASK-091): one person the signed-in user may set an
 * advisor-scope or team-scope goal for on the "Metas" screen -- reduced to
 * the fields that screen actually renders, never a results/performance
 * figure (product-rules.md Independence guide: targets only, never a
 * comparison between advisors).
 */
export interface GoalAudienceMember {
  id: Identifier;
  first_name: string;
  last_name: string;
  advisor_code?: string | null;
}

/** The `sales` fields the goals audience rules need, a superset of `ScopedSale`. */
export interface GoalAudienceSale extends ScopedSale {
  first_name: string;
  last_name: string;
  advisor_code?: string | null;
  disabled?: boolean;
}

const NAME_COLLATOR = new Intl.Collator("es", { sensitivity: "base" });

const fullName = (member: { first_name: string; last_name: string }) =>
  `${member.first_name} ${member.last_name}`;

/**
 * Alphabetical order by full name (acceptance criteria: "in alphabetical
 * order"), locale-aware for Spanish accents/ñ.
 */
export function sortGoalAudience<
  T extends { first_name: string; last_name: string },
>(members: T[]): T[] {
  return [...members].sort((a, b) =>
    NAME_COLLATOR.compare(fullName(a), fullName(b)),
  );
}

const toMember = (sale: GoalAudienceSale): GoalAudienceMember => ({
  id: sale.id,
  first_name: sale.first_name,
  last_name: sale.last_name,
  advisor_code: sale.advisor_code,
});

/**
 * Advisor-scope goal audience (DATA CONTRACT E7 `advisor_goals.scope ===
 * 'advisor'`): direccion sees every active asesora/lider; a lead_manager only
 * her brazo scope (mirrors `isInLeadManagerScope`, the same helper
 * `canAccess.ts` uses for the team screen and TASK-087's RLS matrix); every
 * other role (lider, asesora, back-office) gets an empty list here -- a
 * lider's own advisor-scope audience is her `lider_team_members` rows,
 * already narrowed to her team by RLS/FakeRest (TASK-088), so `GoalsPage`
 * fetches those separately instead of calling this helper for her.
 */
export function getAdvisorGoalAudience(
  actor: Actor,
  sales: GoalAudienceSale[],
): GoalAudienceMember[] {
  if (actor.role !== "direccion" && actor.role !== "lead_manager") {
    return [];
  }
  const eligible = sales.filter(
    (sale) =>
      !sale.disabled && (sale.role === "asesora" || sale.role === "lider"),
  );
  const scoped =
    actor.role === "direccion"
      ? eligible
      : eligible.filter((sale) => isInLeadManagerScope(actor, sale));
  return sortGoalAudience(scoped.map(toMember));
}

/**
 * Team-scope goal audience (`advisor_goals.scope === 'team'`): only direccion
 * and an in-scope lead_manager set a Líder BPO's team goal (ticket
 * description, owner decisions section 3) -- a lider never sees an input for
 * her own team goal, so this always returns an empty list for her (and for
 * asesora/back-office).
 */
export function getTeamGoalAudience(
  actor: Actor,
  sales: GoalAudienceSale[],
): GoalAudienceMember[] {
  if (actor.role !== "direccion" && actor.role !== "lead_manager") {
    return [];
  }
  const lideres = sales.filter(
    (sale) => !sale.disabled && sale.role === "lider",
  );
  const scoped =
    actor.role === "direccion"
      ? lideres
      : lideres.filter((sale) => isInLeadManagerScope(actor, sale));
  return sortGoalAudience(scoped.map(toMember));
}
